import type { Grid } from '@palaccount/spreadsheet';

/**
 * Wave 4C — the line import every document grid shares (INV-H1 first, on the
 * invoice; now the quotation, both orders, the delivery note, the stand-alone
 * credit note, the bill and the direct purchase return too).
 *
 * A small .xlsx/.csv with the columns
 *
 *     Code | Description | Qty | Unit price | Discount %
 *
 * becomes lines. The file is READ by `@palaccount/spreadsheet` (the same
 * reader the data-transfer import uses); this module only decides what the
 * rows mean, and it is pure so a test can hand it a grid.
 *
 * The heading row is matched by name, case- and space-insensitively, in
 * English or Arabic; a file with no recognisable heading is read in the
 * documented column order. A row with neither code nor description is blank
 * and skipped.
 *
 * The same reading serves the clipboard (`parseTsv` below): a block of rows
 * copied out of a spreadsheet or out of another document's grid is the same
 * five columns, tab-separated.
 */
export interface ImportedLine {
  code: string;
  description: string;
  qty: string;
  unitPrice: string;
  discountPct: string;
  /**
   * The row's 1-based position in the source, heading row included, so an
   * error can say "row 4" and mean the row the reader sees in Excel. Absent
   * on lines built by hand (older callers, tests).
   */
  row?: number;
}

type Field = Exclude<keyof ImportedLine, 'row'>;

const HEADINGS: Record<Field, readonly string[]> = {
  code: ['code', 'itemcode', 'item', 'sku', 'الكود', 'الرمز', 'رمزالصنف', 'كودالصنف'],
  description: ['description', 'desc', 'name', 'itemname', 'الوصف', 'البيان', 'الصنف', 'اسمالصنف'],
  qty: ['qty', 'quantity', 'الكمية', 'كمية'],
  unitPrice: ['unitprice', 'price', 'rate', 'السعر', 'سعرالوحدة', 'سعر'],
  discountPct: ['discount', 'discount%', 'discountpct', 'disc', 'disc%', 'الخصم', 'خصم', 'الخصم%', 'نسبةالخصم'],
};

const ORDER: readonly Field[] = ['code', 'description', 'qty', 'unitPrice', 'discountPct'];

/**
 * The template's heading row — the English spelling of each accepted column,
 * in the documented order. Every one of them normalises to an entry of
 * `HEADINGS`, so a template filled in and dropped back is read by name.
 */
export const TEMPLATE_HEADINGS: readonly string[] = ['Code', 'Description', 'Qty', 'Unit price', 'Discount %'];

const normalize = (cell: string) => cell.replace(/[\s_.-]+/g, '').toLowerCase();

/** The column each field lives in, or null when the first row is not a heading. */
function headingMap(row: readonly string[]): Partial<Record<Field, number>> | null {
  const map: Partial<Record<Field, number>> = {};
  let hits = 0;
  row.forEach((cell, index) => {
    const key = normalize(cell);
    for (const field of ORDER) {
      if (map[field] === undefined && HEADINGS[field].includes(key)) {
        map[field] = index;
        hits += 1;
        return;
      }
    }
  });
  // Two recognised headings is a heading row; one could be a line that
  // happens to say "Price".
  return hits >= 2 ? map : null;
}

/** A spreadsheet number cell may carry a thousands separator or a % sign. */
function figure(cell: string | undefined): string {
  return (cell ?? '').replace(/[,\s%]/g, '').trim();
}

/** The rows of a grid as lines, each remembering where it sat in the file. */
export function parseImportedRows(grid: Grid): Array<ImportedLine & { row: number }> {
  const numbered = grid
    .map((cells, index) => ({ cells, row: index + 1 }))
    .filter(({ cells }) => cells.some((cell) => cell.trim() !== ''));
  if (numbered.length === 0) return [];
  const map = headingMap(numbered[0]!.cells);
  const body = map ? numbered.slice(1) : numbered;
  const at = (row: readonly string[], field: Field): string | undefined =>
    map ? (map[field] === undefined ? undefined : row[map[field]]) : row[ORDER.indexOf(field)];

  const lines: Array<ImportedLine & { row: number }> = [];
  for (const { cells, row } of body) {
    const code = (at(cells, 'code') ?? '').trim();
    const description = (at(cells, 'description') ?? '').trim();
    if (code === '' && description === '') continue;
    lines.push({
      code,
      description,
      qty: figure(at(cells, 'qty')),
      unitPrice: figure(at(cells, 'unitPrice')),
      discountPct: figure(at(cells, 'discountPct')),
      row,
    });
  }
  return lines;
}

// ── The clipboard (wave 4C's LineClipboard) ────────────────────────────────

/** One line as "Copy lines" writes it: the template's five columns. */
export interface CopyableLine {
  code: string;
  description: string;
  qty: string;
  unitPrice: string;
  discountPct: string;
}

/** A tab or a line break inside a cell would split it; a space keeps the row whole. */
const tsvCell = (value: string) => value.replace(/[\t\r\n]+/g, ' ').trim();

/**
 * Lines → TSV, heading row first (the template's own headings, so the block
 * pasted into Excel is labelled and pasted back is read by name).
 */
export function linesToTsv(lines: readonly CopyableLine[]): string {
  const rows = [
    TEMPLATE_HEADINGS,
    ...lines.map((line) => [line.code, line.description, line.qty, line.unitPrice, line.discountPct]),
  ];
  return rows.map((row) => row.map(tsvCell).join('\t')).join('\n') + '\n';
}

/**
 * TSV → a grid, the shape Excel and Sheets put on the clipboard. No quoting
 * rules beyond stripping a cell's surrounding double quotes: what a
 * spreadsheet copies is plain tab-separated text.
 */
export function parseTsv(text: string): Grid {
  return text
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .map((line) => line.split('\t').map((cell) => cell.replace(/^"(.*)"$/s, '$1')));
}

/** INV-H1's original reading — the same rows without their positions. */
export function parseImportedLines(grid: Grid): ImportedLine[] {
  return parseImportedRows(grid).map(({ row: _row, ...line }) => line);
}

/** What the resolver needs to know about an item it found by code. */
export interface ResolvedItem {
  id: string;
  name: string;
  unitPrice: string;
}

export interface ResolvedLine<Item extends ResolvedItem = ResolvedItem> extends ImportedLine {
  /** '' when the code matched no item — the line is free text (R14). */
  itemId: string;
  /** The item the code found, for a grid that copies more off it (a unit, a code). */
  item?: Item | null;
}

/** One row the import could not turn into an item line, said out loud. */
export interface ImportRowError {
  /** The row in the source (see `ImportedLine.row`), else the line's position. */
  row: number;
  /** The code as typed; '' when the row had none. */
  code: string;
  /** `unknownCode` — no item has it; `noCode` — this grid needs an item and the row named none. */
  reason: 'unknownCode' | 'noCode';
}

export interface ImportSummary<Item extends ResolvedItem = ResolvedItem> {
  lines: ResolvedLine<Item>[];
  matched: number;
  unknown: number;
  /**
   * Every row that did not resolve to an item, in file order. On a grid that
   * accepts free text these rows ARE in `lines` (as free text) and the list
   * is the warning; on a grid that needs an item they are NOT in `lines`, and
   * the list is the only trace — never a silent drop.
   */
  errors: ImportRowError[];
}

export interface ResolveOptions<Item extends ResolvedItem = ResolvedItem> {
  /**
   * The grid cannot hold a free-text line (a delivery note, a stand-alone
   * credit note, a direct purchase return): rows without a known item are
   * left out of `lines` and reported in `errors`.
   */
  requireItem?: boolean;
  /** Fills a blank description from the item; defaults to the item's name. */
  describe?: (item: Item) => string;
}

/**
 * Codes → items, through whatever search the caller hands in (the editors
 * pass the items list route with `q`). Each distinct code is asked about
 * once; a blank description takes the item's name and a blank price the
 * item's price, exactly as picking the item in the grid would.
 */
export async function resolveImportedLines<Item extends ResolvedItem = ResolvedItem>(
  lines: readonly ImportedLine[],
  findByCode: (code: string) => Promise<Item | null>,
  options: ResolveOptions<Item> = {},
): Promise<ImportSummary<Item>> {
  const cache = new Map<string, Item | null>();
  const out: ResolvedLine<Item>[] = [];
  const errors: ImportRowError[] = [];
  let matched = 0;
  let unknown = 0;
  for (const [index, line] of lines.entries()) {
    const row = line.row ?? index + 1;
    const key = line.code.toLowerCase();
    let item: Item | null = null;
    if (key !== '') {
      if (!cache.has(key)) cache.set(key, await findByCode(line.code));
      item = cache.get(key) ?? null;
      if (item) matched += 1;
      else {
        unknown += 1;
        errors.push({ row, code: line.code, reason: 'unknownCode' });
      }
    } else if (options.requireItem) {
      errors.push({ row, code: '', reason: 'noCode' });
    }
    if (options.requireItem && !item) continue;
    out.push({
      ...line,
      itemId: item?.id ?? '',
      item,
      description: line.description || (item ? (options.describe?.(item) ?? item.name) : '') || line.code,
      unitPrice: line.unitPrice || item?.unitPrice || '',
    });
  }
  return { lines: out, matched, unknown, errors };
}
